import {FC, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button, Checkbox, Icon, Modal, Table} from "semantic-ui-react";
import {LoadingMessage, ErrorMessage} from "@/components/SystemMessages";
import {
    CustomColumnMetadataFunctionType,
    DataStatus,
    IColumnMetadata,
    ICustomColumnsTransformation,
    IDataTableData
} from "@/features/application/api/applicationModels";
import {
    IDataTableFilter,
    IDataTableFilterMap
} from "@/components/DataTable/DataTableModels";
import dataFormatUtil, {DataType} from "@/util/dataFormatUtil";
import dataTableUtil from "@/components/DataTable/dataTableUtils";
import {DataTable} from "@/components/DataTable";
import {Concept} from "@/features/application/api/applicationModels";
import {
    getConceptDriveThruThresholdsConfig,
    IConceptDriveThruThresholdsConfig
} from "../api/coachInsightsConceptConfig";
import {AppState} from "@/state/rootReducer";
import {IImpactedExperiencesResponse, MatchStatus} from "../api/coachInsightsModels";
import coachInsightsAPI from "../api/coachInsightsAPI";
import {DateTime} from "luxon";
import {formatDateString, ISODateString} from "@/util/dateUtil";
import {openTicket} from "@/features/ticket/TicketModalService";
import applicationApi from "@/features/application/api/applicationAPI";
import hj from "@/tracking/hotjar";
import {Receipt24Regular} from "@fluentui/react-icons";
import DataTableMetricValueDisplayElement from "@/components/DataTable/DataTableMetricValueDisplayElement";

interface IProps {
    open: boolean;
    onClose: () => void;
    locationPk: number;
    businessDate: ISODateString;
    locationName: string;
    timeframeStartHour: number;
    timeframeEndHour: number;
}

//TODO: move this to the display formatter once we decide this is useful to see.
function formatTenure(hireDt: string | null, asOfDate: string): string {
    if (!hireDt) return "";
    const hire = DateTime.fromISO(hireDt);
    const asOf = DateTime.fromISO(asOfDate);
    if (!hire.isValid || asOf < hire) return "";
    const totalDays = Math.floor(asOf.diff(hire, "days").days);
    if (totalDays < 30) return `${totalDays}d`;
    const {years, months} = asOf.diff(hire, ["years", "months"]).toObject();
    const y = Math.floor(years ?? 0);
    const m = Math.floor(months ?? 0);
    if (y === 0) return `${m}mo`;
    if (m === 0) return `${y}yr`;
    return `${y}yr ${m}mo`;
}

function buildStaticColumns(): IColumnMetadata[] {
    return [
        {
            key: "cascadeTrigger",
            label: "Cascade Trigger",
            dataType: DataType.String,
            decimalPlaces: 0,
            hasFilter: true,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "checkNumber",
            label: "Check",
            dataType: DataType.String,
            decimalPlaces: 0,
            hasFilter: false,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },

        {
            key: "date",
            label: "Date",
            dataType: DataType.Date,
            decimalPlaces: 0,
            hasFilter: false,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "cashOutTime",
            label: "POS Cash Out Time",
            dataType: DataType.TimeOfDay,
            decimalPlaces: 0,
            hasFilter: true,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "exitTime",
            label: "SOS Exit Time",
            dataType: DataType.TimeOfDay,
            decimalPlaces: 0,
            hasFilter: true,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "channel",
            label: "Channel",
            dataType: DataType.None,
            decimalPlaces: 0,
            hasFilter: true,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "empName",
            label: "Employee",
            dataType: DataType.None,
            decimalPlaces: 0,
            hasFilter: true,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "hireDt",
            label: "Tenure",
            dataType: DataType.None,
            decimalPlaces: 0,
            hasFilter: false,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        }
    ];
}

function buildConfigurableColumns(
    config: IConceptDriveThruThresholdsConfig
): IColumnMetadata[] {
    const {labels} = config;
    return [
        {
            key: "total",
            label: labels.total,
            dataType: DataType.TimeSpan,
            decimalPlaces: 0,
            hasFilter: true,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "menu",
            label: labels.menu,
            dataType: DataType.TimeSpan,
            decimalPlaces: 0,
            hasFilter: true,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "queue",
            label: labels.queue,
            dataType: DataType.TimeSpan,
            decimalPlaces: 0,
            hasFilter: true,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "window",
            label: labels.window,
            dataType: DataType.TimeSpan,
            decimalPlaces: 0,
            hasFilter: true,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "transactionTotal",
            label: "Transaction Total",
            dataType: DataType.Money,
            decimalPlaces: 2,
            hasFilter: false,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        }
    ];
}

function isOverThreshold(timeStr: string, thresholdSeconds: number): boolean {
    const totalSecs = parseInt(timeStr, 10);
    return totalSecs > thresholdSeconds;
}

function buildCustomColumns(
    thresholdSeconds: number,
    configurableColumns: IColumnMetadata[],
    concept: Concept,
    legacySiteBaseUrl: string,
    businessDate: ISODateString,
    locationPk: number
): ICustomColumnsTransformation {
    function timingCell(key: string) {
        const colDef = configurableColumns.find((c) => c.key === key);
        const cellFn: CustomColumnMetadataFunctionType = (
            row,
            _column,
            isMobileScreen
        ) => ({
            element: (
                <Table.Cell
                    key={key}
                    className={
                        row[key] != null &&
                        row[key] !== "" &&
                        isOverThreshold(row[key] as string, thresholdSeconds)
                            ? "over-threshold"
                            : ""
                    }
                >
                    <DataTableMetricValueDisplayElement
                        isMobileScreen={isMobileScreen}
                        metricName={colDef?.label ?? key}
                        metricDisplayValue1={
                            row[key] != null && row[key] !== "" && colDef
                                ? dataFormatUtil.dataTableDisplayValue(
                                      Number(row[key]),
                                      colDef
                                  )
                                : ""
                        }
                    />
                </Table.Cell>
            ),
            sortValue: row[key]
        });
        return {getCustomColumnMetaData: cellFn};
    }

    const checkNumberFn: CustomColumnMetadataFunctionType = (
        row,
        column,
        isMobileScreen
    ) => ({
        element: (
            <Table.Cell key="checkNumber">
                <DataTableMetricValueDisplayElement
                    isMobileScreen={isMobileScreen}
                    metricName={column.label}
                    metricDisplayValue1={
                        row.checkNumber != null ? (
                            <Button
                                className="ticket-receipt-btn"
                                onClick={() => {
                                    applicationApi.trackPageViewOrAction(
                                        window.location.pathname,
                                        "feature=CoachInsightsTransaction"
                                    );
                                    hj(
                                        "event",
                                        "coach_insights_transaction_clicked"
                                    );
                                    openTicket(
                                        businessDate,
                                        locationPk,
                                        row.ticketNbr as number,
                                        concept,
                                        legacySiteBaseUrl
                                    );
                                }}
                            >
                                <Receipt24Regular className="ticket-receipt-icon" />
                                {row.checkNumber}
                            </Button>
                        ) : null
                    }
                />
            </Table.Cell>
        ),
        sortValue: row.checkNumber
    });

    return {
        cascadeTrigger: {
            getCustomColumnMetaData: (row) => ({
                element: (
                    <Table.Cell key="cascadeTrigger">
                        <span
                            className={`trigger-label ${(
                                (row.cascadeTrigger as string) ?? ""
                            ).toLowerCase()}`}
                        >
                            {row.cascadeTrigger}
                        </span>
                    </Table.Cell>
                ),
                sortValue: row.cascadeTrigger
            })
        },
        checkNumber: {getCustomColumnMetaData: checkNumberFn},
        total: timingCell("total"),
        menu: timingCell("menu"),
        queue: timingCell("queue"),
        window: timingCell("window")
    };
}

const ImpactedExperiencesModal: FC<IProps> = ({
    open,
    onClose,
    locationPk,
    businessDate,
    timeframeStartHour,
    timeframeEndHour
}) => {
    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.Loading);
    const [data, setData] = useState<IImpactedExperiencesResponse | null>(null);
    const [showAllCars, setShowAllCars] = useState(false);
    const [filters, setFiltersMap] = useState<IDataTableFilterMap>(new Map());
    const screenWidth = useSelector(
        (state: AppState) => state.application.screenWidth
    );
    const concept = useSelector(
        (state: AppState) => state.application.selectedClientConcept.concept
    );
    const legacySiteBaseUrl = useSelector(
        (state: AppState) => state.application.legacySiteBaseUrl
    );
    const conceptConfig = useMemo(
        () => getConceptDriveThruThresholdsConfig(concept),
        [concept]
    );
    const staticColumns = useMemo(() => buildStaticColumns(), []);
    const configurableColumns = useMemo(
        () => buildConfigurableColumns(conceptConfig),
        [conceptConfig]
    );
    const customColumns = useMemo(
        () =>
            buildCustomColumns(
                conceptConfig.thresholdSeconds,
                configurableColumns,
                concept,
                legacySiteBaseUrl,
                businessDate,
                locationPk
            ),
        [
            conceptConfig.thresholdSeconds,
            configurableColumns,
            concept,
            legacySiteBaseUrl,
            businessDate,
            locationPk
        ]
    );

    useEffect(() => {
        if (!open) return;

        const abortController = new AbortController();
        setDataStatus(DataStatus.Loading);
        setFiltersMap(new Map());

        const fetchData = async () => {
            try {
                const response = await coachInsightsAPI.getImpactedExperiences(
                    locationPk,
                    businessDate,
                    timeframeStartHour,
                    timeframeEndHour,
                    abortController.signal
                );
                setData(response);
                setDataStatus(DataStatus.Loaded);
            } catch (error: any) {
                if (error?.name !== "AbortError") {
                    setDataStatus(DataStatus.Failed);
                }
            }
        };

        fetchData();
        return () => abortController.abort();
    }, [open, locationPk, businessDate, timeframeStartHour, timeframeEndHour]);

    const baseTransactions = useMemo(() => {
        if (!data) return [];
        if (showAllCars) return data.transactions;
        // When not showing all cars: show rows that have SOS data (matched or SOS-only).
        // TransactionOnly rows (no SOS car) are hidden until the user opts into all.
        return data.transactions.filter(
            (t) =>
                t.matchStatus !== MatchStatus.TransactionOnly &&
                t.cascadeTrigger !== ""
        );
    }, [data, showAllCars]);

    const tableData = useMemo<IDataTableData[]>(
        () =>
            dataTableUtil.mapDataTableData(
                baseTransactions.map((t) => ({
                    ...t,
                    checkNumber: t.orderNumber ?? null,
                    ticketNbr: t.ticketNbr ?? null,
                    channel: t.chanDesc ?? "",
                    empName: t.empName ?? null,
                    transactionTotal: t.transactionTotal ?? null,
                    hireDt: formatTenure(t.hireDt, businessDate),
                    date: formatDateString(t.date),
                    exitTime: t.exitTime != null ? t.exitTime / 60 : null,
                    cashOutTime: t.cashOutTime != null ? t.cashOutTime / 60 : null
                }))
            ),
        [baseTransactions]
    );

    function handleSetFilters(key: string, selectedValues: IDataTableFilter): void {
        const newFilters: IDataTableFilterMap = new Map(filters);
        if (selectedValues.size > 0) {
            newFilters.set(key, selectedValues);
        } else {
            newFilters.delete(key);
        }
        setFiltersMap(newFilters);
    }

    function removeAllFilters(): void {
        setFiltersMap(new Map());
    }

    return (
        <Modal
            className="insights-modal"
            open={open}
            onClose={onClose}
            closeIcon
            closeOnDimmerClick={true}
            size="large"
        >
            <Modal.Header>
                <div className="modal-header-content">
                    <div className="modal-header-left">
                        <Icon name="car" />
                        <span className="modal-title">Experiences</span>
                        {data && (
                            <span className="poor-experience-badge">
                                {data.poorExperienceCount} Impacted Experiences
                            </span>
                        )}
                        <Checkbox
                            toggle
                            label="Show All Cars"
                            className="show-all-toggle"
                            checked={showAllCars}
                            onChange={() => {
                                setShowAllCars(!showAllCars);
                                setFiltersMap(new Map());
                            }}
                        />
                    </div>
                </div>
            </Modal.Header>
            <Modal.Content scrolling>
                {dataStatus === DataStatus.Loading && <LoadingMessage />}
                {dataStatus === DataStatus.Failed && (
                    <ErrorMessage text="Failed to load impacted experiences." />
                )}
                {dataStatus === DataStatus.Loaded && data && (
                    <>
                        <div className="cascade-legend">
                            <div className="legend-item">
                                <span className="legend-line cascade-line" />
                                <span>
                                    <b>Cascade Trigger</b>: This car took unusually
                                    long at the window and backed up the cars behind
                                    it, causing a chain of slow service.
                                </span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-line affected-line" />
                                <span>
                                    <b>Affected</b>: This car got stuck waiting
                                    because it pulled in while a cascade trigger was
                                    still at the window.
                                </span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-line isolated-line" />
                                <span>
                                    <b>Isolated</b>: This car took unusually long at
                                    the window, but no other cars were backed up
                                    behind it.
                                </span>
                            </div>
                        </div>

                        <div className="experiences-table">
                            <DataTable
                                staticColumns={staticColumns}
                                configurableColumns={configurableColumns}
                                data={tableData}
                                sortColumnKey="exitTime"
                                itemsPerPage={16}
                                customColumns={customColumns}
                                filters={filters}
                                setFilters={handleSetFilters}
                                removeAllFilters={removeAllFilters}
                                screenWidth={screenWidth}
                                exportFileName="ImpactedExperiences"
                            />
                        </div>
                    </>
                )}
            </Modal.Content>
        </Modal>
    );
};

export default ImpactedExperiencesModal;
